import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { Box, FlexBox, FormDialog, Grid, Icon, Typography } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as deliveryLogActions, selectors as deliveryLogSelectors, KioskSalesSummary } from 'store/deliveryLog';
import { useStyles } from '../styled';
import { SalesReportOverlayProps } from '../types';

export const SalesReportOverlay = (props: SalesReportOverlayProps) => {
  const { clientIntegration, downloadContentAsCSV, onClose, setErrorMessage } = props;

  const classes = useStyles({ isOpen: true })();

  const { getSalesReport, resetSalesReport } = useActions({
    getSalesReport: deliveryLogActions.getSalesReport,
    resetSalesReport: deliveryLogActions.resetSalesReport
  });

  const salesReport = useSelector(deliveryLogSelectors.selectSalesReport);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const isConfirmDisabled = !startDate || !endDate;

  // ON RENDER
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);

    setStartDate(start);
    setEndDate(now);
  }, []);

  // ON FETCH SALES REPORT SUCCESS
  useEffect(() => {
    if (salesReport.data && salesReport.total) {
      const rows = salesReport.data.map((row: KioskSalesSummary) => {
        return [row.kioskName, row.venueName, row.areaName, row.amount];
      });

      const startDateStr = moment(startDate).format('MM/DD/YYYY');
      const startTimeStr = moment(startDate).format('hh:mm:ss A');

      const endDateStr = moment(endDate).format('MM/DD/YYYY');
      const endTimeStr = moment(endDate).format('hh:mm:ss A');

      const csvContent = [
        ['KIS Kiosk Sales Report', '', '', ''],
        ['', '', '', ''],
        ['Start Date', startDateStr, 'Start Time', startTimeStr],
        ['End Date', endDateStr, 'End Time', endTimeStr],
        ['', '', '', ''],
        ['', '', '', ''],
        ['Kiosk Name', 'Venue Name', 'Area Name', 'Amount ($)'],
        ...rows,
        ['Total', '', '', salesReport.total.amount]
      ];

      const fileName = `SalesReport_${clientIntegration.name}_${moment().format('YYYYMMDDHHmmss')}.csv`;

      downloadContentAsCSV(csvContent, fileName);

      resetSalesReport();
      onClose();
    }
  }, [salesReport.data, salesReport.total]);

  // ON FETCH SALES REPORT ERROR
  useEffect(() => {
    if (salesReport.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      resetSalesReport();
    }
  }, [salesReport.error]);

  const onConfirm = () => {
    if (startDate && endDate && startDate > endDate) {
      setErrorMessage('Start date must be before end date.');
      return;
    }

    getSalesReport({
      clientIntegrationId: clientIntegration.id,
      startTime: moment(startDate).utc().format(),
      endTime: moment(endDate).utc().format()
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onConfirm}
      confirmButtonDisabled={isConfirmDisabled}
      confirmButtonIsLoading={salesReport.pending}
      confirmButtonText="Build Report"
      open
      title="Sales Report"
    >
      <Grid direction="column" width="100%" marginTop="10px">
        <FlexBox className={classes.filtersFieldsFlexBox}>
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>Start Date and Time</Typography>
            <div className="my-datepicker-wrapper">
              <div className={classes.datePickerContainer}>
                <DatePicker
                  showTimeSelect
                  selected={startDate}
                  onChange={(d) => setStartDate(d)}
                  placeholderText="MM/DD/YYYY hh:mm aa"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  isClearable
                  customInput={<DatePickerCustomInput />}
                  popperPlacement="bottom-start"
                  calendarClassName="my-custom-calendar"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>
          </Box>

          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>End Date and Time</Typography>
            <div className={`${classes.datePickerContainer} ${classes.rightDatePickerContainer}`}>
              <DatePicker
                showTimeSelect
                selected={endDate}
                onChange={(d) => setEndDate(d)}
                placeholderText="MM/DD/YYYY hh:mm aa"
                dateFormat="MM/dd/yyyy h:mm aa"
                className={classes.datePickerComponent}
                isClearable
                popperPlacement="bottom-start"
                popperProps={{ strategy: 'fixed' }}
                customInput={<DatePickerCustomInput />}
              />
            </div>
          </Box>
        </FlexBox>
      </Grid>
    </FormDialog>
  );
};
